import React, { useState, useMemo } from "react";
import { Calculator, IndianRupee, Percent, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";

export default function EMICalculator() {
  const [principal, setPrincipal] = useState(5000000); // 50L
  const [rate, setRate] = useState(8.5);
  const [tenure, setTenure] = useState(20); // years

  const emi = useMemo(() => {
    const r = rate / 12 / 100;
    const n = tenure * 12;
    if (r === 0) return principal / n;
    return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  }, [principal, rate, tenure]);

  const totalPayment = emi * tenure * 12;
  const totalInterest = totalPayment - principal;

  const formatINR = (val) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(val);

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <div className="flex items-center gap-2 mb-2">
        <Calculator className="h-7 w-7 text-primary" />
        <h1 className="text-3xl font-bold">EMI Calculator</h1>
      </div>
      <p className="text-muted-foreground mb-8">
        Calculate your monthly home loan EMI instantly.
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div>
            <Label className="flex items-center gap-2 mb-2">
              <IndianRupee className="h-4 w-4" /> Loan Amount
            </Label>
            <Input
              type="number"
              value={principal}
              onChange={(e) => setPrincipal(Number(e.target.value))}
              min={100000}
              max={100000000}
            />
            <Slider
              value={[principal]}
              onValueChange={([v]) => setPrincipal(v)}
              min={500000}
              max={50000000}
              step={100000}
              className="mt-2"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>₹5L</span>
              <span>₹5Cr</span>
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-2 mb-2">
              <Percent className="h-4 w-4" /> Interest Rate (% p.a.)
            </Label>
            <Input
              type="number"
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
              min={1}
              max={20}
              step={0.1}
            />
            <Slider
              value={[rate]}
              onValueChange={([v]) => setRate(v)}
              min={5}
              max={15}
              step={0.1}
              className="mt-2"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>5%</span>
              <span>15%</span>
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4" /> Loan Tenure (Years)
            </Label>
            <Input
              type="number"
              value={tenure}
              onChange={(e) => setTenure(Number(e.target.value))}
              min={1}
              max={30}
            />
            <Slider
              value={[tenure]}
              onValueChange={([v]) => setTenure(v)}
              min={1}
              max={30}
              step={1}
              className="mt-2"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1 yr</span>
              <span>30 yrs</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border rounded-xl p-6 bg-primary/5">
            <p className="text-sm text-muted-foreground mb-1">Monthly EMI</p>
            <p className="text-3xl font-bold text-primary">{formatINR(emi)}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="border rounded-lg p-4">
              <p className="text-xs text-muted-foreground mb-1">Total Interest</p>
              <p className="text-lg font-semibold text-red-600">{formatINR(totalInterest)}</p>
            </div>
            <div className="border rounded-lg p-4">
              <p className="text-xs text-muted-foreground mb-1">Total Payment</p>
              <p className="text-lg font-semibold">{formatINR(totalPayment)}</p>
            </div>
          </div>

          {/* Simple breakdown bar */}
          <div className="border rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-2">Payment Breakdown</p>
            <div className="h-4 rounded-full overflow-hidden flex bg-muted">
              <div
                className="bg-primary h-full transition-all"
                style={{ width: `${(principal / totalPayment) * 100}%` }}
              />
              <div
                className="bg-red-400 h-full transition-all"
                style={{ width: `${(totalInterest / totalPayment) * 100}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-primary" /> Principal ({((principal / totalPayment) * 100).toFixed(0)}%)
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-400" /> Interest ({((totalInterest / totalPayment) * 100).toFixed(0)}%)
              </span>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            * This is an indicative calculation. Actual EMI may vary based on bank processing fees, 
            insurance, and other charges.
          </p>
        </div>
      </div>
    </div>
  );
}
